import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { Add } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { Portal } from '../internal/Portal';
import { useDialogBehavior } from '../internal/useDialogBehavior';
import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  /** Called on Escape, the close button, or a backdrop click (when dismissible). */
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Close when the backdrop is clicked. Default true. Escape always calls onClose. */
  dismissible?: boolean;
  /**
   * Element focused on open. Defaults to an element with `data-autofocus`, else the first
   * focusable element after the close button, else the dialog itself.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  hideTitle?: boolean;
  hideCloseButton?: boolean;
  closeLabel?: string;
  /** Use `alertdialog` for urgent confirmations. */
  role?: 'dialog' | 'alertdialog';
  /** Vertical placement. `top` suits command palettes. */
  position?: 'center' | 'top';
  className?: string;
  bodyClassName?: string;
}

/** Accessible modal dialog: portal, focus trap, Escape, scroll lock and focus restoration. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  initialFocusRef,
  hideTitle = false,
  hideCloseButton = false,
  closeLabel = 'Close dialog',
  role = 'dialog',
  position = 'center',
  className,
  bodyClassName,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogBehavior({
    open,
    containerRef: dialogRef,
    initialFocusRef,
    onEscape: onClose,
    lockScroll: true,
  });

  if (!open) return null;

  return (
    <Portal>
      <div
        className={cx(styles.backdrop, position === 'top' && styles.top)}
        role="presentation"
        onMouseDown={(event) => {
          if (dismissible && event.target === event.currentTarget) onClose();
        }}
      >
        <div
          ref={dialogRef}
          role={role}
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          className={cx(styles.dialog, styles[size], className)}
        >
          <div className={cx(styles.header, hideTitle && styles.headerHidden)}>
            <div className={styles.headings}>
              <h2 id={titleId} className={cx(styles.title, hideTitle && 'sr-only')}>
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className={cx(styles.description, hideTitle && 'sr-only')}>
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && !hideTitle && (
              <button
                type="button"
                className={styles.close}
                aria-label={closeLabel}
                data-skip-initial-focus
                onClick={onClose}
              >
                <Add
                  size={22}
                  color="currentColor"
                  className={styles.closeIcon}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
          <div className={cx(styles.body, bodyClassName)}>{children}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}
